import type { FC } from 'react'
import { useEffect } from 'react'

import { Textarea } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import type { Employee } from '@/resources/employees'

import SelectedUser from '../SelectedUser'

import SectionAddress from './SectionAddress'
import SectionProducts from './SectionProducts'

import './section-user.scss'
import './step-confirm.scss'

const StepConfirm: FC<{
  employee: Employee | undefined
  cost: number
  isMaxAmountExceeded: boolean
  isForLocation?: boolean
  selectMandatoryProducts: () => void
  regressWizard?: () => void
  onActiveStepSet?: (newActiveStep: number) => void
}> = ({ cost, isMaxAmountExceeded, isForLocation, employee, regressWizard, onActiveStepSet }) => {
  const shopCtx = useShopNewOrderContext()
  const [comment, setComment] = shopCtx.comment
  const [, setActiveStepName] = shopCtx.activeStepName
  const [policyId] = shopCtx.selectedPolicyId
  const [, setMandatoryProducts] = shopCtx.mandatoryProducts
  const [selectedProductIds] = shopCtx.selectedProductIds
  const [productQuantityById, setProductQuantityById] = shopCtx.productQuantityById

  // Get mandatory products
  const mandatoryProductsQuery = useQuery(
    queries.mandatoryProducts(policyId!, selectedProductIds || []),
    () => ecomPolicyResources.getMandatoryProducts(policyId!, selectedProductIds || []),
    {
      enabled: false,
      onSuccess: data => {
        setMandatoryProducts(data.map(product => ({ ...product, isMandatory: 1 })))
        setProductQuantityById({
          ...productQuantityById,
          ...data.reduce((acc, item) => ({ ...acc, [item.id]: 1 }), {}),
        })
      },
    }
  )

  useEffect(() => {
    const handleMandatoryProductsFetch = async () => {
      await mandatoryProductsQuery.refetch()
      setActiveStepName('CONFIRMATION')
    }

    handleMandatoryProductsFetch()
  }, [])

  return (
    <div className="step-confirm">
      <div className="step-confirm__sections">
        <section className="step-confirm__user">
          <SelectedUser employee={employee} isForLocation={isForLocation} />
        </section>

        <SectionAddress />
        <section>
          <h2>
            <Translation id="Comment" />
          </h2>

          <div className="step-confirm__comment">
            <Textarea
              isFullHeight
              id="new-order-comment"
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </div>
        </section>

        <SectionProducts
          isMaxAmountExceeded={isMaxAmountExceeded}
          cost={cost}
          regressWizard={regressWizard}
          onActiveStepSet={onActiveStepSet}
        />
      </div>
    </div>
  )
}

export default StepConfirm
